import { ProcessingStatus } from './enums';

/** Fields `sourceDocuments.update` may change. */
const updateAttributes = [
  'pageCount',
  'documentTypeId',
  'subjectHint',
  'sourceStyleId',
  'institutionId',
  'institutionHint',
  'year',
  'paperTitle',
  'paperReference',
  'rightsNote',
  'handwrittenAnswerPages',
] as const;

export const SourceDocumentAttributes = {
  Update: updateAttributes,
};

/**
 * The states a person can send a paper back to the digitiser from, by
 * readmitting it to `pending`. A paper being digitised belongs to the worker
 * holding it, and a digitised one stays digitised: the state machine has no
 * edge back to `pending` from either. A paper held for review goes back once
 * its handwriting is decided, since digitising it again undecided would only
 * hold it again. A paper whose worker is gone is sent as it is; see
 * `isStalledProcessing`.
 */
export const RETRYABLE_SOURCE_DOCUMENT_STATUSES = [
  ProcessingStatus.Pending,
  ProcessingStatus.Deferred,
  ProcessingStatus.Failed,
  ProcessingStatus.Rejected,
] as const;

/**
 * How long a document may sit in `processing` before another run takes it back.
 * Comfortably past the fifteen minutes a Lambda can live, so the worker holding
 * it is certainly gone rather than merely slow.
 */
export const STALLED_CLAIM_MS = 30 * 60 * 1000;

/** Whether a paper's worker died holding it, so a digitise run may claim it back. */
export const isStalledProcessing = (
  document: { status: ProcessingStatus; createdAt: number; updatedAt: number | null },
  now: number
): boolean =>
  document.status === ProcessingStatus.Processing &&
  (document.updatedAt ?? document.createdAt) < now - STALLED_CLAIM_MS;

/**
 * The key in a document digitisation run's `stats` counting the times it was taken back
 * from a worker that stopped holding it. The digitiser counts; Retry clears it.
 */
export const STALLED_RECLAIMS_STAT = 'stalledReclaims';
